import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths";
import type { ApiResponse } from "jadro-pico/dtos/common/ApiResponse";
import type { OperationHistoryDto } from "jadro-pico/dtos/pico_com/OperationHistoryDto";
import type { PicoAppDto } from "jadro-pico/apps/PicoAppDto";
import type { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import type { AppAuthState } from "../../../Redux/AppAuthReducer";
import PageLayout from "../Layouts/PageLayout/PageLayout";
import BackHeaderPage from "../Layouts/PageLayout/BackHeaderPage";
import SectionCard from "../SectionCard/SectionCard";
import { SectionCardVariantEnum } from "../SectionCard/SectionCardVariantEnum";
import List from "../List/List";
import ListItem from "../List/ListItem/ListItem";
import ListItemLeadingElement from "../List/ListItem/ListItemLeadingElement";
import Icon from "../Icon/Icon";
import ErrorState from "../ErrorState/ErrorState";
import Skeleton from "../Skeleton/Skeleton";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import { useApiQuery } from "../../../Api/useApiQuery";
import { useResource } from "../../../Resources/useResource";
import { RequestQueueResources } from "../../../Resources/RequestQueueResources";
import { APPS_CATALOG_QUERY_KEY, OPERATION_PER_APP_QUERY_KEY } from "../../../Api/queryKeys";
import { useOutbox } from "../../../Offline/useRequestQueue";
import { buildHubSections, type HubSection } from "../../../Offline/hubSections";
import { useRequestQueueRow } from "./RequestQueueRow";
import { useQueueBack } from "./useQueueBack";
import { useRoutePaths } from "../../../Routes/useRoutePaths";
import { PicoComRoutes } from "../../../Routes/PicoComRoutes";

interface RequestQueueHubProps {
  apiUrl: string;
  app: PicoAppEnum;
  /** Path to this app's own queue; every other app is reached at its own origin. */
  ownQueuePath: string;
}

const RequestQueueHub: React.FC<RequestQueueHubProps> = ({ apiUrl, app, ownQueuePath }) => {
  const resources = useResource(RequestQueueResources);
  const navigate = useNavigate();
  const renderRow = useRequestQueueRow();
  const goBack = useQueueBack(useRoutePaths(PicoComRoutes).PROFILE);
  const userUuid = useSelector((s: { auth: AppAuthState }) => s.auth.user?.sub);
  const outbox = useOutbox(userUuid ?? "");

  const history = useApiQuery<ApiResponse<OperationHistoryDto[]>>({
    url: `${apiUrl}${ApiPaths.operations.prefix}${ApiPaths.operations.perApp}`,
    queryKey: OPERATION_PER_APP_QUERY_KEY,
  });
  const apps = useApiQuery<PicoAppDto[]>({
    url: `${apiUrl}${ApiPaths.apps.prefix}`,
    queryKey: APPS_CATALOG_QUERY_KEY,
    staleTime: Infinity,
  });

  const rows = history.data?.data ?? [];
  const catalog = apps.data ?? [];
  const sections = buildHubSections({ app, rows, outbox, catalog });
  // The history failing leaves nothing to show; the catalog failing only matters for a section
  // that actually needs an address to be opened, so the two are asked separately and named
  // separately — telling someone their history failed while it is on screen is just wrong.
  const historyMissing = history.error !== null && rows.length === 0;
  const linksMissing =
    apps.error !== null &&
    sections.some((section) => section.id !== app && section.hostsQueue && !section.feUrl);

  const failure = historyMissing
    ? resources.errorHeadline
    : linksMissing
      ? resources.linksErrorHeadline
      : undefined;

  const openerFor = (section: HubSection): (() => void) | undefined => {
    if (section.id === app) return () => void navigate(ownQueuePath);
    if (!section.hostsQueue || !section.feUrl) return undefined;
    const target = `${section.feUrl}${ownQueuePath}`;
    return () => window.location.assign(target);
  };

  const retry = {
    label: resources.retryLabel,
    onClick: () => {
      void history.refetch();
      void apps.refetch();
    },
  };

  const body = (): React.ReactElement => {
    // Sections are always there now, so "nothing yet" is about their contents, not their count.
    const nothingToShow = sections.every((section) => section.items.length === 0);
    if (nothingToShow && history.isLoading) return <Skeleton height={200} />;
    if (nothingToShow && failure !== undefined)
      return <ErrorState headline={failure} retry={retry} />;

    return (
      <>
        {sections.map((section) => {
          const open = openerFor(section);
          return (
            <SectionCard key={section.id} variant={SectionCardVariantEnum.SURFACE} flush>
              <List dividers>
                <ListItem
                  headline={section.name}
                  supportingText={resources.title}
                  leadingElement={
                    <ListItemLeadingElement>
                      <Icon
                        icon={ThemeIconEnum.ARROW_FORWARD}
                        color={open ? ThemeColorEnum.PRIMARY : ThemeColorEnum.ON_SURFACE_VARIANT}
                      />
                    </ListItemLeadingElement>
                  }
                  onClick={open}
                />
                {section.items.map(renderRow)}
              </List>
            </SectionCard>
          );
        })}
        {failure !== undefined && <ErrorState headline={failure} retry={retry} />}
      </>
    );
  };

  return (
    <PageLayout maxWidth={720} gap={16} background={ThemeColorEnum.SURFACE_VARIANT}>
      <BackHeaderPage title={resources.title} backLabel={resources.closeAriaLabel} onBack={goBack}>
        {body()}
      </BackHeaderPage>
    </PageLayout>
  );
};

export default RequestQueueHub;
